/**
 * English messages (document 15, section 5.1). Keys are sentence-level and grouped by screen; a
 * plural has `_one` and `_other` forms in English and all six CLDR forms in Arabic (`_zero`,
 * `_one`, `_two`, `_few`, `_many`, `_other`). Placeholders are `{{name}}`; never build a sentence
 * from pieces, so Arabic word order and agreement stay the translator's.
 */
import { areasEn } from './areas/index.js';

export const en = {
  ...areasEn,
  common: {
    save: 'Save',
    saving: 'Saving…',
    saveAndClose: 'Save and close',
    saveAndNew: 'Save and new',
    cancel: 'Cancel',
    close: 'Close',
    new: 'New',
    edit: 'Edit',
    apply: 'Apply',
    import: 'Import',
    runReport: 'Run report',
    viewRegister: 'View register',
    loading: 'Loading…',
    none: 'None',
    yes: 'Yes',
    no: 'No',
    on: 'On',
    off: 'Off',
    inactive: 'Inactive',
    optional: 'Optional',
  },
  language: {
    en: 'English',
    ar: 'العربية',
    both: 'English and Arabic',
  },
  shell: {
    client: 'Client:',
    selectClient: 'Select a client',
    switchClient: 'Switch client',
    findClient: 'Find a client',
    signInDisabled: 'Sign-in is disabled (no VITE_OIDC_ISSUER).',
    noClients: 'You have no clients yet. Ask an admin to add you to one.',
    viewAllClients: 'View all clients',
    accountantTools: 'Accountant tools',
    help: 'Help',
    keyboardShortcuts: 'Keyboard shortcuts',
    settings: 'Settings',
    signOut: 'Sign out',
    unread_one: '{{count}} unread',
    unread_other: '{{count}} unread',
    selectClientTitle: 'Select a client',
    selectClientHint: 'Use the client switcher in the top bar to choose whose books to open.',
  },
  roles: {
    outss_admin: 'Outss admin',
    outss_accountant: 'Accountant',
    client_owner: 'Owner',
    client_viewer: 'Viewer',
  },
  arabicField: {
    name: 'Arabic name',
    description: 'Arabic description',
    hintName: 'Optional. Shown to Arabic readers; the English name is used when this is empty.',
    hintDescription:
      'Optional. Shown to Arabic readers; the English description is used when this is empty.',
    noArabic: 'No Arabic letters: Arabic readers will see this exactly as typed.',
  },
  accounts: {
    title: 'Chart of accounts',
    suggestArabic: 'Suggest Arabic names',
    filterPlaceholder: 'Filter by name or number',
    filterLabel: 'Filter accounts',
    includeInactive: 'Include inactive',
    missingArabic: 'Missing Arabic name',
    count_one: '{{count}} account',
    count_other: '{{count}} accounts',
    loadError: 'Could not load accounts',
    noMatch: 'No accounts match your filter',
    number: 'Number',
    name: 'Name',
    accountType: 'Account type',
    detailType: 'Detail type',
    balanceIn: 'Balance ({{currency}})',
    balance: 'Balance',
    action: 'Action',
    makeInactive: 'Make inactive',
    makeActive: 'Make active',
    setOpeningBalance: 'Set opening balance',
    systemAccount: 'System account',
    controlAccountTip: 'Control account: posted to by documents, not journal entries.',
    systemAccountTip: 'System account: cannot be made inactive.',
    moreActions: 'More actions for {{name}}',
    activeAgain: '{{name}} is active again.',
    nowInactive: '{{name}} is now inactive.',
    inactiveTitle: 'Make {{name}} inactive?',
    inactiveBody:
      'Inactive accounts are hidden from lists and cannot be used on new transactions. Their history stays in reports. Accounts with a balance must be cleared first.',
  },
  accountDrawer: {
    newTitle: 'New account',
    editTitle: 'Edit account',
    newDescription: 'Add an account to the chart.',
    accountType: 'Account type',
    detailType: 'Detail type',
    lockedType: 'Fixed once the account has transactions.',
    systemAccount: 'System account.',
    name: 'Name',
    number: 'Number',
    description: 'Description',
    subAccount: 'Make this a sub-account',
    parent: 'Parent account',
    chooseAccount: 'Choose an account',
    currency: 'Currency',
    currencyHint: 'The currency of the bank or card account; its statements are in it.',
    homeCurrency: '{{code}} – home currency',
    defaultTaxCode: 'Default {{tax}} code',
    saved: 'Account {{code}} {{name}} saved.',
    enterName: 'Enter a name.',
    enterNumber: 'Enter a number (up to 20 letters, digits, dots or dashes).',
    controlNote: 'Manual journal entries cannot post to this account.',
    chooseParent: 'Choose the parent account.',
  },
  suggest: {
    title: 'Suggest Arabic names',
    description:
      'Accounts without an Arabic name, with a suggestion from the chart template or the glossary. Check each name before applying; untick any you want to leave in English.',
    allHave: 'Every account has an Arabic name.',
    account: 'Account',
    arabicName: 'Arabic name',
    source: 'Source',
    sourceTemplate: 'Chart template',
    sourceGlossary: 'Glossary',
    sourceArabicName: 'Name is Arabic',
    sourceNone: 'None',
    needsEnglish: 'The name is Arabic: give the account an English name too.',
    applyThis: 'Apply the Arabic name of {{name}}',
    arabicNameOf: 'Arabic name of {{name}}',
    apply_one: 'Apply {{count}} name',
    apply_other: 'Apply {{count}} names',
    added_one: '{{count}} Arabic name added.',
    added_other: '{{count}} Arabic names added.',
    kept_one: '{{count}} already had one and was kept.',
    kept_other: '{{count}} already had one and were kept.',
  },
  home: {
    title: 'Home',
    description:
      'Your business for the selected period. Items that need your attention are listed first.',
    period: 'Period',
    leaveBusinessView: 'Leave Business view',
    upload: 'Upload bills, receipts and invoices',
    loadError: 'Could not load your summary',
    selectClientHint: 'Use the client switcher in the top bar.',
    inTheBank: 'In the bank',
    bankAccounts_one: '{{count}} account',
    bankAccounts_other: '{{count}} accounts',
    owedToYou: 'Owed to you',
    youOwe: 'You owe',
    overdueOpen: '{{overdue}} overdue · {{open}} open',
    profit: 'Profit',
    changeOn: '{{change}} on {{previous}}',
    moneyOwedToYou: 'Money owed to you',
    moneyYouOwe: 'Money you owe',
    needsAttention: 'Needs your attention',
    approvals_one: 'document is waiting for your approval',
    approvals_other: 'documents are waiting for your approval',
    rejected_one: 'draft was returned by your accountant with comments',
    rejected_other: 'drafts were returned by your accountant with comments',
    submitted_one: 'draft is with your accountant for approval',
    submitted_other: 'drafts are with your accountant for approval',
    inbox_one: 'upload is being processed by your accountant',
    inbox_other: 'uploads are being processed by your accountant',
    allCaughtUp: 'No items need your attention.',
    allCaughtUpOwner: 'Upload bills, receipts and invoices as you receive them.',
    cashPosition: 'Cash position',
    card: '(card)',
    setOpeningOf: 'Set opening balance of {{name}}',
    balancesNote: 'Balances in the books, as recorded by your accountant.',
    selectToSetOpening: 'Select an account to set its opening balance.',
    openInvoices_one: '{{count}} open invoice',
    openInvoices_other: '{{count}} open invoices',
    openBills_one: '{{count}} open bill',
    openBills_other: '{{count}} open bills',
    overdue: 'Overdue',
    viewInvoices: 'View invoices',
    viewBills: 'View bills',
    returnLabel: 'Return {{label}}',
    refundable: 'Refundable',
    payable: 'Payable',
    due: 'due {{date}}',
    taxReturns: '{{tax}} returns',
    noReturnDue: 'No {{tax}} return is due.',
    recentUploads: 'Recent uploads',
    allDocuments: 'All documents',
    nothingUploaded:
      'Nothing uploaded yet. Send bills, receipts and invoices to your accountant from Documents.',
    recordedAsExpense: '{{status}} as an expense for {{name}}',
    recordedAsInvoice: '{{status}} as an invoice for {{name}}',
    recordedAsBill: '{{status}} as a bill for {{name}}',
  },
  vatStatus: {
    open: 'Period open',
    due: 'Due',
    overdue: 'Overdue',
    filed: 'Filed',
    paid: 'Paid',
  },
  inboxStatus: {
    received: 'Received',
    in_review: 'In review',
    recorded: 'Recorded',
    rejected: 'Not recorded',
  },
  periods: {
    this_month: 'This month',
    last_month: 'Last month',
    this_quarter: 'This quarter',
    this_year: 'This financial year',
    prev_this_month: 'Last month',
    prev_last_month: 'Month before',
    prev_this_quarter: 'Last quarter',
    prev_this_year: 'Last financial year',
  },
  dashboard: {
    profitAndLoss: 'Profit and loss',
    viewReport: 'View report',
    income: 'Income',
    expenses: 'Expenses',
    netProfit: 'Net profit',
    thisPeriod: 'This period',
    viewExpenses: 'View expenses',
    noExpenses: 'No expenses in this period.',
    everythingElse: 'Everything else',
    totalSpent: 'Total spent',
    invoices: 'Invoices',
    unpaid: 'Unpaid',
    paid: 'Paid',
    overdue: 'Overdue',
    notDueYet: 'Not due yet',
    notDeposited: 'Not deposited',
    deposited30: 'Deposited (last 30 days)',
    bankAccounts: 'Bank accounts',
    goToBanking: 'Go to banking',
    noBankAccounts: 'No bank or credit card accounts yet.',
    toReview: '{{count}} to review',
    bankBalance: 'Bank balance',
    inOutss: 'In Outss Accounting',
    setOpeningBalance: 'Set opening balance',
    sales: 'Sales',
    salesChart: 'Income per month for the last twelve months',
    showFigures: 'Show figures',
    hideFigures: 'Hide figures',
  },
  businessMenu: {
    draftForApproval: '{{label}} (draft for approval)',
  },
  notifications: {
    title: 'Notifications',
    unreadAria: 'Notifications, {{count}} unread',
    markAllRead: 'Mark all as read',
    none: 'You have no notifications.',
    settings: 'Notification settings',
  },
  businessView: {
    label: 'Business view',
    on: 'Business view on',
    back: 'Back to the Accountant view',
    see: 'See what the client sees',
    cashSummary: 'Cash summary',
    cashBalance: 'Cash balance',
    acrossAccounts_one: 'Across {{count}} bank and cash account',
    acrossAccounts_other: 'Across {{count}} bank and cash accounts',
    netPosition: 'Net position',
    netPositionHint: 'Cash balance plus amounts owed to you, less amounts you owe.',
    shortcuts: 'Shortcuts',
    shortcut: {
      upload: 'Upload documents',
      newInvoice: 'New invoice',
      newBill: 'New bill',
      newExpense: 'New expense',
      receivePayment: 'Receive payment',
      invoices: 'Invoices',
      bills: 'Bills',
      reports: 'Reports',
    },
    noOverdueInvoices: 'No overdue invoices',
    noOverdueBills: 'No overdue bills',
    noReturnDue: 'No return is due.',
    thisMonth: 'This month · {{from}} – {{to}}',
    netLoss: 'Net loss',
    comparedWithLastMonth: '{{change}} compared with last month',
    viewAll: 'View all',
  },
  demo: {
    asOwner: 'You are in the demo as a business owner',
    asAccountant: 'You are in the demo as an accountant',
    rest: 'with sample companies shared with other visitors. Post anything; the books are reset from time to time.',
    switchToOwner: 'Switch to business owner',
    switchToAccountant: 'Switch to accountant',
    createAccount: 'Create your account',
  },
  search: {
    label: 'Search',
    placeholder: 'Search transactions, contacts, accounts',
    results: 'Search results',
    searching: 'Searching…',
    noResults: 'No results for “{{q}}”',
    noRecent: 'No recent transactions',
    selectClientFirst: 'Select a client first',
    recent: 'Recent transactions',
    transactions: 'Transactions',
    contacts: 'Customers and vendors',
    accounts: 'Accounts',
    items: 'Products and services',
  },
  files: {
    download: 'Download',
    dropHere: 'Drag and drop files here, or',
    browse: 'browse',
    types: 'PDF or photo (PNG, JPEG, WebP, HEIC)',
    upTo: '{{types}}, up to {{mb}} MB',
    chooseFiles: 'Choose files',
    fromInbox: 'From the document inbox:',
    uploadedBy: '{{kind}} uploaded by {{who}} on {{date}}',
    someone: 'someone',
    savingKeeps: 'Saving keeps the file attached and marks the upload recorded.',
  },
  inboxKind: {
    bill: 'Bill',
    receipt: 'Receipt',
    invoice: 'Invoice',
    other: 'Other',
  },
  decision: {
    optional: '(optional)',
  },
  approvals: {
    title: 'Approvals',
    staffDescription:
      'Drafts the client submitted for your approval, and drafts you asked the client to approve.',
    clientDescription:
      'Invoices and bills your accountant asked you to approve, and drafts you submitted to them.',
    waiting: 'Waiting',
    history: 'History',
    loadError: 'Could not load approvals',
    nothingWaiting: 'Nothing is waiting for approval',
    noDecisions: 'No decisions yet',
    staffHint: 'Use "Ask the client to approve" on a draft invoice or bill.',
    clientHint: 'Your accountant will ask you here when a draft needs your approval.',
    waitingForYou: 'Waiting for you',
    nothingForYou: 'Nothing needs your decision.',
    waitingClientOrPost: 'Waiting for the client, or ready to post',
    waitingAccountant: 'Waiting for your accountant',
    nothingHere: 'Nothing here.',
    document: 'Document',
    contact: 'Customer or vendor',
    date: 'Date',
    total: 'Total',
    asked: 'Asked',
    decision: 'Decision',
    state: 'State',
    action: 'Action',
    invoiceNo: 'Invoice {{number}}',
    billNo: 'Bill {{number}}',
    draft: '(draft)',
    openToPost: 'Open to post',
    readyToPost: 'Approved by the client, ready to post',
    awaitingClient: 'Awaiting the client',
    awaitingAccountant: 'Awaiting the accountant',
    status_pending: 'Awaiting approval',
    status_approved: 'Approved',
    status_rejected: 'Rejected',
    status_cancelled: 'Withdrawn',
    docInvoice: 'invoice {{number}} for {{name}}',
    docBill: 'bill {{number}} for {{name}}',
    approve: 'Approve',
    approveAndPost: 'Approve and post',
    reject: 'Reject',
    withdraw: 'Withdraw',
    withdrawConfirm: 'Withdraw this approval request?',
    withdrawn: 'Approval request withdrawn.',
    approvedPosted: 'Approved: the {{doc}} is posted.',
    approvedAccountant: 'Approved: your accountant will post the {{doc}}.',
    rejected: 'Rejected: the {{doc}} goes back with your comment.',
    approveTitle: 'Approve the {{doc}}?',
    approveStaff:
      'Approving posts it to the books with the next number. Your comment goes to the client.',
    approveClient:
      'Your accounting team posts it after your approval. Your comment goes to your accountant.',
    rejectTitle: 'Reject the {{doc}}?',
    rejectDescription:
      'It stays a draft. Say what needs to change; the comment goes with the rejection.',
    comment: 'Comment',
    sentToClient: 'Sent to the client for approval.',
    askedBy: 'asked by {{who}} on {{date}}',
    submittedBy: 'submitted by {{who}} on {{date}}',
    decidedBy: '{{status}} by {{who}} on {{date}}',
    note: 'Note: {{text}}',
    commentText: 'Comment: {{text}}',
    changeAndResend: 'Change the draft and save it to send it for approval again.',
    wantClientInvoice: 'Want the client to confirm this invoice before it is posted?',
    wantClientBill: 'Want the client to confirm this bill before it is posted?',
    askClient: 'Ask the client to approve',
    askTitleInvoice: 'Ask the client to approve this invoice?',
    askTitleBill: 'Ask the client to approve this bill?',
    askDescription:
      'Client owners are notified and see it on their home page. It is not posted until they approve it (or you withdraw the request).',
    sendForApproval: 'Send for approval',
    noteToClient: 'Note to the client',
  },
  attachments: {
    title: 'Attachments',
    attached: '{{name}} attached.',
    filesAttached_one: '{{count}} file attached.',
    filesAttached_other: '{{count}} files attached.',
    removed: '{{name}} removed.',
    remove: 'Remove {{name}}',
    removeConfirm: 'Remove {{name}} from this record?',
    uploading: 'Uploading…',
    dropToAttach: 'Drop files to attach, or',
  },
  messages: {
    title: 'Messages',
    staffDescription:
      'Your conversation with {{client}}. Everyone at the client and at Outss sees it.',
    thisClient: 'this client',
    clientDescription:
      'Your conversation with your Outss accountant. Everyone at your company and at Outss sees it.',
    loadError: 'Could not load the messages.',
    none: 'No messages yet',
    staffEmpty: 'Write to the client below; their owners are told in the app and by email.',
    clientEmpty: 'Ask your accountant anything below; they are told straight away.',
    loadEarlier: 'Load earlier messages',
    you: 'You',
    client: 'Client',
    message: 'Message',
    placeholder: 'Write a message',
    sendHint: 'Ctrl+Enter to send',
    sending: 'Sending…',
    send: 'Send',
    tooLong: 'At most 4,000 characters',
  },
  kindHint: {
    bill: 'To pay',
    receipt: 'Already paid',
    invoice: 'Issued to a customer',
    other: 'Anything else',
  },
  inbox: {
    title: 'Documents',
    staffDescription:
      'Bills, receipts and invoices uploaded by the client and the team. Turn each into a draft bill or expense, or process it with the AI Accountant; the file stays attached.',
    clientDescription:
      'Upload bills, receipts and invoices for your accountant. You will see here when each one is recorded.',
    chip_open: 'To review',
    chip_recorded: 'Recorded',
    chip_rejected: 'Not recorded',
    chip_all: 'All',
    today: 'Today',
    yesterday: 'Yesterday',
    lastWeek: 'Last 7 days',
    older: 'Older',
    sentToAi: '{{name}} sent to the AI Accountant.',
    open: 'Open',
    uploadedOne: '{{name}} uploaded. Your accountant has been told.',
    uploadedMany_one: '{{count}} file uploaded. Your accountant has been told.',
    uploadedMany_other: '{{count}} files uploaded. Your accountant has been told.',
    statusChanged: '{{name}}: {{status}}.',
    upload: 'Upload',
    whatIsIt: 'What is it?',
    documentType: 'Document type',
    noteForAccountant: 'Note for your accountant',
    note: 'Note',
    notePlaceholder: 'For example: paid by card, split with the Dubai branch',
    uploading: 'Uploading…',
    dropHere: 'Drag and drop bills, receipts and invoices here, or',
    status: 'Status',
    searchLabel: 'Search documents',
    searchPlaceholder: 'Search file, note or person',
    loadError: 'Could not load the documents',
    noMatch: 'No documents match your search',
    nothingToReview: 'Nothing to review',
    noDocuments: 'No documents here',
    tryDifferent: 'Try a different file name, note or person.',
    uploadedAppear: 'Uploaded bills, receipts and invoices appear here.',
    ownersUpload: 'Client owners upload bills, receipts and invoices here.',
    rejectTitle: 'Mark {{name}} as not recordable?',
    thisUpload: 'this upload',
    rejectDescription:
      'The person who uploaded it is told why. You can move it back to review later.',
    reject: 'Reject',
    reason: 'Reason',
    by: 'by {{name}}',
    notRecorded: 'Not recorded: {{reason}}',
    docBill: 'Bill {{number}}',
    docExpense: 'Expense {{number}}',
    docInvoice: 'Invoice {{number}}',
    draft: '(draft)',
    inAi: 'In AI Accountant',
    openInAi: 'Open in AI Accountant',
    processWithAi: 'Process with AI Accountant',
    createBill: 'Create bill',
    createExpense: 'Create expense',
    moreActions: 'More actions',
    markInReview: 'Mark in review',
    backToReceived: 'Back to received',
    cannotRecord: 'Cannot be recorded…',
  },
  mobile: {
    home: 'Home',
    reports: 'Reports',
    chat: 'Chat',
    yourAccountant: 'Your accountant',
    askAi: 'Ask AI',
    askOutssAi: 'Ask Outss AI',
    more: 'More',
    chooseCompany: 'Choose a company',
    invoice: 'Invoice',
    bill: 'Bill',
    new: 'New',
    upload: 'Upload bills, receipts and invoices',
    report: 'Report',
    taxes: 'Taxes',
    notifications: 'Notifications',
    documents: 'Documents',
    sales: 'Sales',
    expenses: 'Expenses',
    switchCompany: 'Switch company',
    switch: 'Switch',
    demo: 'Demo',
    india: 'India',
    uae: 'UAE',
    yourBusiness: 'Your business',
    documentsSubtitle: 'Uploads and approvals',
    salesSubtitle: 'Invoices and customers',
    expensesSubtitle: 'Bills, expenses and vendors',
    yourCompany: 'Your company',
    customerStatements: 'Customer statements',
    companySettings: 'Company settings',
    language: 'Language',
    signOut: 'Sign out',
    version: 'Outss Accounting · version {{version}}',
    restartTitle: 'Restart the app',
    restartBody: 'Close and reopen Outss Accounting to read it right to left.',
    restartBodyLtr: 'Close and reopen Outss Accounting to read it left to right.',
    ok: 'OK',
  },
  ui: {
    addNew: 'Add new',
    openCalendar: 'Open calendar',
    datePlaceholder: 'dd/mm/yyyy',
    close: 'Close',
    dismiss: 'Dismiss',
    newVersion: 'A new version of Outss Accounting is available.',
    reload: 'Reload',
    comingSoon: 'Coming soon',
    pageNotFound: 'Page not found',
    notAvailable: "This feature isn't available yet.",
    noPage: 'There is no page at {{path}}.',
    goToDashboard: 'Go to the dashboard',
    noMatches: 'No matches',
  },
  profile: {
    language: 'Language',
    languageHint: 'The language the app uses for you. Each person chooses their own.',
    languageSaved: 'Language saved.',
  },
};

export type Messages = typeof en;
